import React, { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import { activityAPI } from '../lib/api';
import { formatDate, getActionLabel, cn } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import {
    RefreshCw,
    UserPlus,
    LogIn,
    Package,
    Edit,
    Trash2,
    FileSpreadsheet,
    Download,
    Upload,
    Unlink
} from 'lucide-react';

const getActionIcon = (action) => {
    const icons = {
        inscription: UserPlus,
        connexion: LogIn,
        creation_commande: Package,
        modification_commande: Edit,
        suppression_commande: Trash2,
        configuration_sheets: FileSpreadsheet,
        synchronisation_sheets: Download,
        export_sheets: Upload,
        deconnexion_sheets: Unlink,
    };
    return icons[action] || Package;
};

const getActionColor = (action) => {
    const colors = {
        inscription: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400',
        connexion: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
        creation_commande: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
        modification_commande: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
        suppression_commande: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
        configuration_sheets: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400',
        synchronisation_sheets: 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900/30 dark:text-cyan-400',
        export_sheets: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-400',
        deconnexion_sheets: 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400',
    };
    return colors[action] || 'bg-gray-100 text-gray-800';
};

export default function History() {
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);

    const fetchLogs = async () => {
        setLoading(true);
        try {
            const response = await activityAPI.getLogs(100);
            setLogs(response.data);
        } catch (error) {
            console.error('Error fetching logs:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchLogs();
    }, []);

    // Group logs by date
    const groupedLogs = logs.reduce((acc, log) => {
        const date = log.timestamp.split('T')[0];
        if (!acc[date]) {
            acc[date] = [];
        }
        acc[date].push(log);
        return acc;
    }, {});

    const formatDateHeader = (dateStr) => {
        const date = new Date(dateStr);
        const today = new Date();
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);

        if (date.toDateString() === today.toDateString()) {
            return "Aujourd'hui";
        } else if (date.toDateString() === yesterday.toDateString()) {
            return 'Hier';
        } else {
            return date.toLocaleDateString('fr-FR', {
                weekday: 'long',
                day: 'numeric',
                month: 'long',
                year: 'numeric',
            });
        }
    };

    return (
        <Layout>
            <div className="space-y-6" data-testid="history-page">
                {/* Header */}
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div>
                        <h1 className="text-3xl font-semibold tracking-tight">Historique</h1>
                        <p className="text-muted-foreground mt-1">
                            Journal des activités et modifications
                        </p>
                    </div>
                    <Button
                        variant="outline"
                        onClick={fetchLogs}
                        disabled={loading}
                        data-testid="refresh-logs-btn"
                    >
                        <RefreshCw className={cn('w-4 h-4 mr-2', loading && 'animate-spin')} />
                        Actualiser
                    </Button>
                </div>

                {/* Activity Timeline */}
                <Card className="card-luxury">
                    <CardHeader>
                        <CardTitle className="text-lg">Journal d'Activités</CardTitle>
                    </CardHeader>
                    <CardContent>
                        {loading ? (
                            <div className="flex justify-center py-12">
                                <div className="spinner" />
                            </div>
                        ) : logs.length === 0 ? (
                            <div className="text-center py-12 text-muted-foreground">
                                Aucune activité enregistrée
                            </div>
                        ) : (
                            <ScrollArea className="h-[600px] pr-4">
                                <div className="space-y-8">
                                    {Object.entries(groupedLogs).map(([date, dayLogs]) => (
                                        <div key={date}>
                                            <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-4">
                                                {formatDateHeader(date)}
                                            </h3>
                                            <div className="space-y-4">
                                                {dayLogs.map((log) => {
                                                    const Icon = getActionIcon(log.action);
                                                    return (
                                                        <div
                                                            key={log.id}
                                                            className="flex gap-4 p-4 rounded-[2px] bg-muted/30 hover:bg-muted/50 transition-colors"
                                                            data-testid={`activity-log-${log.id}`}
                                                        >
                                                            <div className={cn('w-10 h-10 rounded-[2px] flex items-center justify-center shrink-0', getActionColor(log.action))}>
                                                                <Icon className="w-5 h-5" />
                                                            </div>
                                                            <div className="flex-1 min-w-0">
                                                                <div className="flex items-start justify-between gap-2">
                                                                    <div>
                                                                        <p className="font-medium">
                                                                            {log.user_name}
                                                                        </p>
                                                                        <Badge variant="outline" className="mt-1">
                                                                            {getActionLabel(log.action)}
                                                                        </Badge>
                                                                    </div>
                                                                    <span className="text-xs text-muted-foreground shrink-0">
                                                                        {formatDate(log.timestamp).split(' ')[1]}
                                                                    </span>
                                                                </div>
                                                                {log.details && (
                                                                    <p className="text-sm text-muted-foreground mt-2">
                                                                        {log.details}
                                                                    </p>
                                                                )}
                                                                {log.order_number && (
                                                                    <p className="text-xs text-muted-foreground mt-1">
                                                                        Commande: <span className="mono">{log.order_number}</span>
                                                                    </p>
                                                                )}
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </ScrollArea>
                        )}
                    </CardContent>
                </Card>
            </div>
        </Layout>
    );
}
